import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Mail,
  Download,
  Search,
  Trash2,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  MoreVertical,
  Filter,
  Tag,
  ChevronLeft,
  ChevronRight,
  X,
  FileText,
  Plus,
  RefreshCw,
  Settings,
  MessageSquare,
  Bell,
  Zap,
  Phone,
  Globe,
  Calendar,
  Edit2
} from 'lucide-react';
import './WebsiteLeadsManagement.css';

// Helper function matching App.js api pattern
const api = (path, opts = {}) => {
  return fetch(`/api${path}`, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {})
    }
  }).then(res => res.json());
};

const WebsiteLeadsManagement = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });

  // Modals
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Settings state
  const [formSettings, setFormSettings] = useState({
    notifyAdminEmail: true,
    adminEmails: [],
    notifyWebhook: false,
    webhookUrl: '',
    sendAutoReply: false,
    autoReplySubject: 'Thanks for contacting us!',
    autoReplyBody: '',
    defaultTags: []
  });
  const [popupSettings, setPopupSettings] = useState({
    isEnabled: false,
    title: 'Wait! Before you go...',
    subtitle: '',
    description: '',
    buttonText: 'Subscribe',
    placeholder: 'Enter your email',
    successMessage: 'Thanks for subscribing!',
    imageUrl: '',
    imagePosition: 'none',
    showNameField: false,
    namePlaceholder: 'Enter your name',
    showPhoneField: false,
    phonePlaceholder: 'Enter your phone number',
    enabledPages: [],
    delaySeconds: 0,
    showOnMobile: true,
    showOncePerSession: true,
    cookieDays: 7
  });

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(activeTab !== 'all' && { type: activeTab.toUpperCase() })
      });

      const data = await api(`/admin/website-leads?${params}`);

      if (data.success) {
        setLeads(data.leads);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  }, [pagination.page, pagination.limit, searchQuery, statusFilter, activeTab]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await api('/admin/website-leads/stats');
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch settings - merge with defaults to handle missing fields
  const fetchSettings = useCallback(async () => {
    try {
      const [formData, popupData] = await Promise.all([
        api('/admin/website-leads/settings/form'),
        api('/admin/website-leads/settings/popup')
      ]);

      if (formData.success) setFormSettings(formData.settings);
      if (popupData.success) {
        // Merge with defaults to ensure new fields have values even if backend doesn't return them
        setPopupSettings(prev => ({
          ...prev,
          ...popupData.settings,
          // Ensure boolean fields are never undefined
          showNameField: popupData.settings.showNameField ?? prev.showNameField ?? false,
          showPhoneField: popupData.settings.showPhoneField ?? prev.showPhoneField ?? false,
          showOnMobile: popupData.settings.showOnMobile ?? prev.showOnMobile ?? true,
          showOncePerSession: popupData.settings.showOncePerSession ?? prev.showOncePerSession ?? true,
          isEnabled: popupData.settings.isEnabled ?? prev.isEnabled ?? false
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchStats(), fetchSettings()]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Update lead status
  const updateLeadStatus = async (leadId, status) => {
    setActionLoading(leadId);
    try {
      const data = await api(`/admin/website-leads/${leadId}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
      if (data.success) {
        fetchLeads();
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating lead:', error);
    }
    setActionLoading(null);
  };

  // Delete lead
  const deleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    setActionLoading(leadId);
    try {
      const data = await api(`/admin/website-leads/${leadId}`, {
        method: 'DELETE'
      });
      if (data.success) {
        fetchLeads();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
    setActionLoading(null);
  };

  // Bulk delete
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} leads?`)) return;
    try {
      const data = await api('/admin/website-leads/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ leadIds: selectedIds })
      });
      if (data.success) {
        setSelectedIds([]);
        fetchLeads();
        fetchStats();
      }
    } catch (error) {
      console.error('Error bulk deleting:', error);
    }
  };

  // Bulk tag
  const bulkTag = async (tags, action) => {
    try {
      const data = await api('/admin/website-leads/bulk-tag', {
        method: 'POST',
        body: JSON.stringify({ leadIds: selectedIds, tags, action })
      });
      if (data.success) {
        setSelectedIds([]);
        setShowTagModal(false);
        fetchLeads();
      }
    } catch (error) {
      console.error('Error bulk tagging:', error);
    }
  };

  // Export leads
  const exportLeads = async () => {
    try {
      const params = new URLSearchParams({
        ...(statusFilter && { status: statusFilter }),
        ...(activeTab !== 'all' && { type: activeTab.toUpperCase() })
      });

      window.open(`/api/admin/website-leads/export?${params}`, '_blank');
    } catch (error) {
      console.error('Error exporting leads:', error);
    }
  };

  // Save settings
  const saveSettings = async () => {
    setActionLoading('settings');
    try {
      await Promise.all([
        api('/admin/website-leads/settings/form', {
          method: 'PUT',
          body: JSON.stringify(formSettings)
        }),
        api('/admin/website-leads/settings/popup', {
          method: 'PUT',
          body: JSON.stringify(popupSettings)
        })
      ]);
      setShowSettingsModal(false);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
    setActionLoading(null);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === leads.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(leads.map(l => l.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      NEW: 'bg-blue-100 text-blue-700',
      CONTACTED: 'bg-yellow-100 text-yellow-700',
      QUALIFIED: 'bg-green-100 text-green-700',
      CONVERTED: 'bg-purple-100 text-purple-700',
      UNSUBSCRIBED: 'bg-gray-100 text-gray-700',
      SPAM: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getTypeBadge = (type) => {
    const styles = {
      CONTACT: { bg: 'bg-indigo-100 text-indigo-700', icon: MessageSquare },
      NEWSLETTER: { bg: 'bg-green-100 text-green-700', icon: Mail },
      EXIT_POPUP: { bg: 'bg-orange-100 text-orange-700', icon: Zap }
    };
    return styles[type] || { bg: 'bg-gray-100 text-gray-700', icon: Users };
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="website-leads-management">
      {/* Header */}
      <div className="leads-header">
        <div className="header-left">
          <h1><Globe size={24} /> Website Leads</h1>
          <p className="header-subtitle">Manage leads from your website forms and popups</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={fetchLeads}>
            <RefreshCw size={16} />
          </button>
          <button className="btn-secondary" onClick={exportLeads}>
            <Download size={16} /> Export
          </button>
          <button className="btn-primary" onClick={() => setShowSettingsModal(true)}>
            <Settings size={16} /> Settings
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon total"><Users size={20} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total Leads</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon contact"><MessageSquare size={20} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.byType?.contact || 0}</span>
              <span className="stat-label">Contact Form</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon newsletter"><Mail size={20} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.byType?.newsletter || 0}</span>
              <span className="stat-label">Newsletter</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon popup"><Zap size={20} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.byType?.popup || 0}</span>
              <span className="stat-label">Exit Popup</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon today"><Clock size={20} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.byPeriod?.today || 0}</span>
              <span className="stat-label">Today</span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="leads-tabs">
        {['all', 'contact', 'newsletter', 'exit_popup'].map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab); setPagination(p => ({ ...p, page: 1 })); }}
          >
            {tab === 'all' ? 'All Leads' :
             tab === 'contact' ? 'Contact Form' :
             tab === 'newsletter' ? 'Newsletter' : 'Exit Popup'}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="leads-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="NEW">New</option>
          <option value="CONTACTED">Contacted</option>
          <option value="QUALIFIED">Qualified</option>
          <option value="CONVERTED">Converted</option>
          <option value="UNSUBSCRIBED">Unsubscribed</option>
          <option value="SPAM">Spam</option>
        </select>

        {selectedIds.length > 0 && (
          <div className="bulk-actions">
            <span>{selectedIds.length} selected</span>
            <button className="btn-sm" onClick={() => setShowTagModal(true)}>
              <Tag size={14} /> Tag
            </button>
            <button className="btn-sm danger" onClick={bulkDelete}>
              <Trash2 size={14} /> Delete
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="leads-table-container">
        {loading ? (
          <div className="loading-state">
            <RefreshCw className="spin" size={24} />
            <p>Loading leads...</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <h3>No leads found</h3>
            <p>Leads will appear here when visitors submit forms on your website.</p>
          </div>
        ) : (
          <table className="leads-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === leads.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Contact</th>
                <th>Type</th>
                <th>Status</th>
                <th>Tags</th>
                <th>Source</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => {
                const typeInfo = getTypeBadge(lead.type);
                const TypeIcon = typeInfo.icon;
                return (
                  <tr key={lead.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(lead.id)}
                        onChange={() => toggleSelect(lead.id)}
                      />
                    </td>
                    <td>
                      <div className="lead-contact">
                        <span className="lead-email">{lead.email}</span>
                        {lead.name && <span className="lead-name">{lead.name}</span>}
                        {lead.phone && <span className="lead-phone"><Phone size={12} /> {lead.phone}</span>}
                      </div>
                    </td>
                    <td>
                      <span className={`type-badge ${typeInfo.bg}`}>
                        <TypeIcon size={12} />
                        {lead.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <select
                        className={`status-select ${getStatusBadge(lead.status)}`}
                        value={lead.status}
                        onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                        disabled={actionLoading === lead.id}
                      >
                        <option value="NEW">New</option>
                        <option value="CONTACTED">Contacted</option>
                        <option value="QUALIFIED">Qualified</option>
                        <option value="CONVERTED">Converted</option>
                        <option value="UNSUBSCRIBED">Unsubscribed</option>
                        <option value="SPAM">Spam</option>
                      </select>
                    </td>
                    <td>
                      <div className="lead-tags">
                        {(lead.tags || []).slice(0, 2).map(tag => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                        {(lead.tags || []).length > 2 && (
                          <span className="tag more">+{lead.tags.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="lead-source" title={lead.source}>
                        {lead.source ? new URL(lead.source).pathname : '-'}
                      </span>
                    </td>
                    <td>
                      <span className="lead-date">{formatDate(lead.createdAt)}</span>
                    </td>
                    <td>
                      <div className="lead-actions">
                        <button
                          className="action-btn"
                          onClick={() => { setSelectedLead(lead); setShowLeadModal(true); }}
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="action-btn danger"
                          onClick={() => deleteLead(lead.id)}
                          title="Delete"
                          disabled={actionLoading === lead.id}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={pagination.page === 1}
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
          >
            <ChevronLeft size={16} />
          </button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Lead Detail Modal */}
      {showLeadModal && selectedLead && (
        <div className="modal-overlay" onClick={() => setShowLeadModal(false)}>
          <div className="modal-content lead-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Lead Details</h2>
              <button className="close-btn" onClick={() => setShowLeadModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="lead-detail-grid">
                <div className="detail-item">
                  <label>Email</label>
                  <span>{selectedLead.email}</span>
                </div>
                {selectedLead.name && (
                  <div className="detail-item">
                    <label>Name</label>
                    <span>{selectedLead.name}</span>
                  </div>
                )}
                {selectedLead.phone && (
                  <div className="detail-item">
                    <label>Phone</label>
                    <span>{selectedLead.phone}</span>
                  </div>
                )}
                <div className="detail-item">
                  <label>Type</label>
                  <span>{selectedLead.type.replace('_', ' ')}</span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <span className={`status-badge ${getStatusBadge(selectedLead.status)}`}>
                    {selectedLead.status}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Date</label>
                  <span>{formatDate(selectedLead.createdAt)}</span>
                </div>
                {selectedLead.subject && (
                  <div className="detail-item full-width">
                    <label>Subject</label>
                    <span>{selectedLead.subject}</span>
                  </div>
                )}
                {selectedLead.message && (
                  <div className="detail-item full-width">
                    <label>Message</label>
                    <p className="lead-message">{selectedLead.message}</p>
                  </div>
                )}
                {selectedLead.source && (
                  <div className="detail-item full-width">
                    <label>Source URL</label>
                    <span className="source-url">{selectedLead.source}</span>
                  </div>
                )}
                {selectedLead.utmSource && (
                  <div className="detail-item">
                    <label>UTM Source</label>
                    <span>{selectedLead.utmSource}</span>
                  </div>
                )}
                {selectedLead.utmMedium && (
                  <div className="detail-item">
                    <label>UTM Medium</label>
                    <span>{selectedLead.utmMedium}</span>
                  </div>
                )}
                {selectedLead.utmCampaign && (
                  <div className="detail-item">
                    <label>UTM Campaign</label>
                    <span>{selectedLead.utmCampaign}</span>
                  </div>
                )}
                <div className="detail-item full-width">
                  <label>Tags</label>
                  <div className="lead-tags">
                    {(selectedLead.tags || []).map(tag => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                    {(!selectedLead.tags || selectedLead.tags.length === 0) && (
                      <span className="no-tags">No tags</span>
                    )}
                  </div>
                </div>
                {selectedLead.notes && (
                  <div className="detail-item full-width">
                    <label>Notes</label>
                    <p className="lead-notes">{selectedLead.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="modal-content settings-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Lead Settings</h2>
              <button className="close-btn" onClick={() => setShowSettingsModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* Notification Settings */}
              <div className="settings-section">
                <h3><Bell size={18} /> Notifications</h3>
                <div className="setting-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={formSettings.notifyAdminEmail}
                      onChange={e => setFormSettings(s => ({ ...s, notifyAdminEmail: e.target.checked }))}
                    />
                    Email admin when new lead arrives
                  </label>
                </div>
                {formSettings.notifyAdminEmail && (
                  <div className="setting-row">
                    <label>Admin Email Addresses</label>
                    <input
                      type="text"
                      placeholder="email1@example.com, email2@example.com"
                      value={(formSettings.adminEmails || []).join(', ')}
                      onChange={e => setFormSettings(s => ({
                        ...s,
                        adminEmails: e.target.value.split(',').map(e => e.trim()).filter(Boolean)
                      }))}
                    />
                  </div>
                )}
                <div className="setting-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={formSettings.notifyWebhook}
                      onChange={e => setFormSettings(s => ({ ...s, notifyWebhook: e.target.checked }))}
                    />
                    Send webhook notification
                  </label>
                </div>
                {formSettings.notifyWebhook && (
                  <div className="setting-row">
                    <label>Webhook URL</label>
                    <input
                      type="url"
                      placeholder="https://..."
                      value={formSettings.webhookUrl || ''}
                      onChange={e => setFormSettings(s => ({ ...s, webhookUrl: e.target.value }))}
                    />
                  </div>
                )}
              </div>

              {/* Auto-reply Settings */}
              <div className="settings-section">
                <h3><Mail size={18} /> Auto-Reply (Contact Form)</h3>
                <div className="setting-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={formSettings.sendAutoReply}
                      onChange={e => setFormSettings(s => ({ ...s, sendAutoReply: e.target.checked }))}
                    />
                    Send auto-reply email to contact form leads
                  </label>
                </div>
                {formSettings.sendAutoReply && (
                  <>
                    <div className="setting-row">
                      <label>Subject</label>
                      <input
                        type="text"
                        value={formSettings.autoReplySubject || ''}
                        onChange={e => setFormSettings(s => ({ ...s, autoReplySubject: e.target.value }))}
                      />
                    </div>
                    <div className="setting-row">
                      <label>Body</label>
                      <textarea
                        rows={4}
                        value={formSettings.autoReplyBody || ''}
                        onChange={e => setFormSettings(s => ({ ...s, autoReplyBody: e.target.value }))}
                        placeholder="Thank you for contacting us..."
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Popup Settings */}
              <div className="settings-section">
                <h3><Zap size={18} /> Exit Intent Popup</h3>
                <div className="setting-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={popupSettings.isEnabled}
                      onChange={e => setPopupSettings(s => ({ ...s, isEnabled: e.target.checked }))}
                    />
                    Enable exit intent popup
                  </label>
                </div>
                {popupSettings.isEnabled && (
                  <>
                    {/* Layout Section */}
                    <div className="settings-subsection">
                      <h4 className="subsection-title">Layout</h4>
                      <div className="setting-row">
                        <label>Popup Layout</label>
                        <div className="wlm-layout-options">
                          {[
                            { value: 'full', label: 'Centered', desc: 'Form only, centered' },
                            { value: 'split-left', label: 'Split Left', desc: 'Image left, form right' },
                            { value: 'split-right', label: 'Split Right', desc: 'Form left, image right' },
                            { value: 'minimal', label: 'Minimal', desc: 'Compact centered' }
                          ].map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              className={`wlm-layout-btn ${(popupSettings.layout || 'full') === opt.value ? 'active' : ''}`}
                              onClick={() => setPopupSettings(s => ({ ...s, layout: opt.value }))}
                            >
                              <span className="wlm-layout-label">{opt.label}</span>
                              <span className="wlm-layout-desc">{opt.desc}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="settings-subsection">
                      <h4 className="subsection-title">Content</h4>
                      <div className="setting-row">
                        <label>Title</label>
                        <input
                          type="text"
                          value={popupSettings.title}
                          onChange={e => setPopupSettings(s => ({ ...s, title: e.target.value }))}
                        />
                      </div>
                      <div className="setting-row">
                        <label>Subtitle (optional)</label>
                        <input
                          type="text"
                          value={popupSettings.subtitle || ''}
                          onChange={e => setPopupSettings(s => ({ ...s, subtitle: e.target.value }))}
                          placeholder="e.g., Get 10% off your first order"
                        />
                      </div>
                      <div className="setting-row">
                        <label>Description (optional)</label>
                        <textarea
                          rows={2}
                          value={popupSettings.description || ''}
                          onChange={e => setPopupSettings(s => ({ ...s, description: e.target.value }))}
                          placeholder="e.g., Join our newsletter for exclusive deals and updates"
                        />
                      </div>
                      <div className="setting-row inline">
                        <div>
                          <label>Button Text</label>
                          <input
                            type="text"
                            value={popupSettings.buttonText}
                            onChange={e => setPopupSettings(s => ({ ...s, buttonText: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label>Email Placeholder</label>
                          <input
                            type="text"
                            value={popupSettings.placeholder}
                            onChange={e => setPopupSettings(s => ({ ...s, placeholder: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="setting-row inline">
                        <div>
                          <label>Success Message</label>
                          <input
                            type="text"
                            value={popupSettings.successMessage}
                            onChange={e => setPopupSettings(s => ({ ...s, successMessage: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label>Dismiss Text</label>
                          <input
                            type="text"
                            value={popupSettings.dismissText || 'No thanks'}
                            onChange={e => setPopupSettings(s => ({ ...s, dismissText: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Discount/Promo Section */}
                    <div className="settings-subsection">
                      <h4 className="subsection-title">Discount Incentive</h4>
                      <div className="setting-row">
                        <label>
                          <input
                            type="checkbox"
                            checked={!!popupSettings.showDiscount}
                            onChange={e => setPopupSettings(s => ({ ...s, showDiscount: e.target.checked }))}
                          />
                          Show discount code
                        </label>
                        <span className="setting-hint">Display a promo code to incentivize signups</span>
                      </div>
                      {!!popupSettings.showDiscount && (
                        <>
                          <div className="setting-row inline nested">
                            <div>
                              <label>Discount Code</label>
                              <input
                                type="text"
                                value={popupSettings.discountCode || ''}
                                onChange={e => setPopupSettings(s => ({ ...s, discountCode: e.target.value.toUpperCase() }))}
                                placeholder="e.g., WELCOME10"
                                style={{ fontFamily: 'monospace', letterSpacing: '1px' }}
                              />
                            </div>
                            <div>
                              <label>Label</label>
                              <input
                                type="text"
                                value={popupSettings.discountLabel || 'Use code for 10% off:'}
                                onChange={e => setPopupSettings(s => ({ ...s, discountLabel: e.target.value }))}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Image Section - only for split layouts */}
                    {(popupSettings.layout === 'split-left' || popupSettings.layout === 'split-right') && (
                      <div className="settings-subsection">
                        <h4 className="subsection-title">Image</h4>
                        <div className="setting-row">
                          <label>Image URL</label>
                          <input
                            type="url"
                            placeholder="https://example.com/image.jpg"
                            value={popupSettings.imageUrl || ''}
                            onChange={e => setPopupSettings(s => ({ ...s, imageUrl: e.target.value }))}
                          />
                          <span className="setting-hint">Recommended: 600x800px or similar portrait ratio</span>
                        </div>
                        <div className="setting-row">
                          <label>
                            <input
                              type="checkbox"
                              checked={!!popupSettings.imageOverlay}
                              onChange={e => setPopupSettings(s => ({ ...s, imageOverlay: e.target.checked }))}
                            />
                            Add dark overlay to image
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Styling Section */}
                    <div className="settings-subsection">
                      <h4 className="subsection-title">Styling</h4>
                      <div className="setting-row inline">
                        <div>
                          <label>Primary Color</label>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="color"
                              value={popupSettings.primaryColor || '#6366f1'}
                              onChange={e => setPopupSettings(s => ({ ...s, primaryColor: e.target.value }))}
                              style={{ width: '40px', height: '36px', padding: '2px', cursor: 'pointer' }}
                            />
                            <input
                              type="text"
                              value={popupSettings.primaryColor || '#6366f1'}
                              onChange={e => setPopupSettings(s => ({ ...s, primaryColor: e.target.value }))}
                              style={{ flex: 1, fontFamily: 'monospace' }}
                            />
                          </div>
                        </div>
                        <div>
                          <label>Background Color</label>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="color"
                              value={popupSettings.backgroundColor || '#ffffff'}
                              onChange={e => setPopupSettings(s => ({ ...s, backgroundColor: e.target.value }))}
                              style={{ width: '40px', height: '36px', padding: '2px', cursor: 'pointer' }}
                            />
                            <input
                              type="text"
                              value={popupSettings.backgroundColor || '#ffffff'}
                              onChange={e => setPopupSettings(s => ({ ...s, backgroundColor: e.target.value }))}
                              style={{ flex: 1, fontFamily: 'monospace' }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="setting-row inline">
                        <div>
                          <label>Border Radius (px)</label>
                          <input
                            type="number"
                            min="0"
                            max="32"
                            value={popupSettings.borderRadius || '16'}
                            onChange={e => setPopupSettings(s => ({ ...s, borderRadius: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label>Button Style</label>
                          <select
                            value={popupSettings.buttonStyle || 'filled'}
                            onChange={e => setPopupSettings(s => ({ ...s, buttonStyle: e.target.value }))}
                          >
                            <option value="filled">Filled</option>
                            <option value="outline">Outline</option>
                            <option value="rounded">Rounded Pill</option>
                          </select>
                        </div>
                      </div>
                      <div className="setting-row">
                        <label>Animation</label>
                        <select
                          value={popupSettings.animation || 'fade'}
                          onChange={e => setPopupSettings(s => ({ ...s, animation: e.target.value }))}
                        >
                          <option value="fade">Fade In</option>
                          <option value="slide-up">Slide Up</option>
                          <option value="scale">Scale</option>
                          <option value="none">None</option>
                        </select>
                      </div>
                    </div>

                    {/* Form Fields Section */}
                    <div className="settings-subsection">
                      <h4 className="subsection-title">Form Fields</h4>
                      <div className="setting-row">
                        <label>
                          <input
                            type="checkbox"
                            checked={!!popupSettings.showNameField}
                            onChange={e => setPopupSettings(s => ({ ...s, showNameField: e.target.checked }))}
                          />
                          Collect visitor's name
                        </label>
                      </div>
                      {!!popupSettings.showNameField && (
                        <div className="setting-row nested">
                          <label>Name Field Placeholder</label>
                          <input
                            type="text"
                            value={popupSettings.namePlaceholder || 'Your name'}
                            onChange={e => setPopupSettings(s => ({ ...s, namePlaceholder: e.target.value }))}
                          />
                        </div>
                      )}
                      <div className="setting-row">
                        <label>
                          <input
                            type="checkbox"
                            checked={!!popupSettings.showPhoneField}
                            onChange={e => setPopupSettings(s => ({ ...s, showPhoneField: e.target.checked }))}
                          />
                          Collect visitor's phone number
                        </label>
                      </div>
                      {!!popupSettings.showPhoneField && (
                        <div className="setting-row nested">
                          <label>Phone Field Placeholder</label>
                          <input
                            type="text"
                            value={popupSettings.phonePlaceholder || 'Your phone number'}
                            onChange={e => setPopupSettings(s => ({ ...s, phonePlaceholder: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>

                    {/* Display Settings Section */}
                    <div className="settings-subsection">
                      <h4 className="subsection-title">Display Settings</h4>
                      <div className="setting-row inline">
                        <div>
                          <label>Delay (seconds)</label>
                          <input
                            type="number"
                            min="0"
                            value={popupSettings.delaySeconds}
                            onChange={e => setPopupSettings(s => ({ ...s, delaySeconds: parseInt(e.target.value) || 0 }))}
                          />
                        </div>
                        <div>
                          <label>Don't show again for (days)</label>
                          <input
                            type="number"
                            min="1"
                            value={popupSettings.cookieDays}
                            onChange={e => setPopupSettings(s => ({ ...s, cookieDays: parseInt(e.target.value) || 7 }))}
                          />
                        </div>
                      </div>
                      <div className="setting-row">
                        <label>
                          <input
                            type="checkbox"
                            checked={popupSettings.showOnMobile}
                            onChange={e => setPopupSettings(s => ({ ...s, showOnMobile: e.target.checked }))}
                          />
                          Show on mobile devices
                        </label>
                      </div>
                      <div className="setting-row">
                        <label>
                          <input
                            type="checkbox"
                            checked={popupSettings.showOncePerSession}
                            onChange={e => setPopupSettings(s => ({ ...s, showOncePerSession: e.target.checked }))}
                          />
                          Show only once per session
                        </label>
                      </div>
                      <div className="setting-row">
                        <label>Enabled Pages (leave empty for all pages)</label>
                        <input
                          type="text"
                          placeholder="/, /pricing, /features"
                          value={(popupSettings.enabledPages || []).join(', ')}
                          onChange={e => setPopupSettings(s => ({
                            ...s,
                            enabledPages: e.target.value.split(',').map(p => p.trim()).filter(Boolean)
                          }))}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Default Tags */}
              <div className="settings-section">
                <h3><Tag size={18} /> Default Tags</h3>
                <div className="setting-row">
                  <label>Tags to auto-apply to new leads</label>
                  <input
                    type="text"
                    placeholder="website-lead, needs-followup"
                    value={(formSettings.defaultTags || []).join(', ')}
                    onChange={e => setFormSettings(s => ({
                      ...s,
                      defaultTags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                    }))}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowSettingsModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={saveSettings}
                disabled={actionLoading === 'settings'}
              >
                {actionLoading === 'settings' ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tag Modal */}
      {showTagModal && (
        <BulkTagModal
          onClose={() => setShowTagModal(false)}
          onApply={bulkTag}
          selectedCount={selectedIds.length}
        />
      )}
    </div>
  );
};

// Bulk Tag Modal Component
const BulkTagModal = ({ onClose, onApply, selectedCount }) => {
  const [tags, setTags] = useState('');
  const [action, setAction] = useState('add');

  const handleApply = () => {
    const tagArray = tags.split(',').map(t => t.trim()).filter(Boolean);
    if (tagArray.length > 0) {
      onApply(tagArray, action);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content tag-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Tag {selectedCount} Leads</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          <div className="setting-row">
            <label>Action</label>
            <select value={action} onChange={e => setAction(e.target.value)}>
              <option value="add">Add Tags</option>
              <option value="remove">Remove Tags</option>
            </select>
          </div>
          <div className="setting-row">
            <label>Tags (comma-separated)</label>
            <input
              type="text"
              placeholder="hot-lead, followup-needed"
              value={tags}
              onChange={e => setTags(e.target.value)}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleApply}>Apply</button>
        </div>
      </div>
    </div>
  );
};

export default WebsiteLeadsManagement;
